import React from 'react';
import { Pressable, StyleProp, StyleSheet, View, ViewStyle } from 'react-native';

import Text from '../Text';
import { useStyles } from '~/hooks';
import { FONT_SIZE_XS } from '~/constants/typography';
import { scaleSize } from '~/constants/scale';
import { Theme } from '~/theme';

export type SegmentedOption<T extends string> = {
    value: T;
    // Translated via the core Text component (i18n key or plain text).
    label: string;
    // Overrides the selected fill where the choice carries its own meaning — buy/sell take the
    // app's price colours rather than the brand accent.
    color?: string;
};

type Props<T extends string> = {
    options: SegmentedOption<T>[];
    value: T;
    onChange: (value: T) => void;
    containerStyle?: StyleProp<ViewStyle>;
};

/**
 * One control, n mutually exclusive segments — as opposed to Chip, which is a badge or an
 * independently selectable filter. The distinction is the point: a row of Chips reads as
 * multi-select, which is what a pair of them was wrongly saying here.
 */
export default function SegmentedControl<T extends string>({ options, value, onChange, containerStyle }: Props<T>) {
    const styles = useStyles(stylesGenerator);

    return (
        <View style={[styles.container, containerStyle]}>
            {options.map((option, index) => {
                const selected = option.value === value;
                return (
                    <Pressable
                        key={option.value}
                        onPress={() => onChange(option.value)}
                        style={({ pressed }) => [
                            styles.segment,
                            index > 0 ? styles.divider : null,
                            selected ? styles.segmentSelected : null,
                            selected && option.color ? { backgroundColor: option.color } : null,
                            pressed && !selected ? styles.pressed : null,
                        ]}
                    >
                        <Text
                            fontType={selected ? 'LATO_BOLD' : 'LATO_REGULAR'}
                            style={StyleSheet.flatten([styles.label, selected ? styles.labelSelected : null])}
                        >
                            {option.label}
                        </Text>
                    </Pressable>
                );
            })}
        </View>
    );
}

const stylesGenerator = (theme: Theme) =>
    StyleSheet.create({
        container: {
            flexDirection: 'row',
            alignSelf: 'flex-start',
            borderRadius: scaleSize(12),
            borderWidth: 1,
            // theme.border, not tabIconDefault: the latter is a bright mid-grey that reads as a
            // halo once it rings the filled segment, rather than as a hairline.
            borderColor: theme.border,
            // Clips the selected segment's fill to the container's rounded corners.
            overflow: 'hidden',
        },
        segment: {
            paddingHorizontal: scaleSize(14),
            paddingVertical: scaleSize(4),
        },
        divider: {
            borderLeftWidth: 1,
            borderLeftColor: theme.border,
        },
        segmentSelected: {
            backgroundColor: theme.button,
        },
        pressed: {
            opacity: 0.6,
        },
        label: {
            fontSize: FONT_SIZE_XS,
            color: theme.textSecondary,
        },
        labelSelected: {
            color: theme.buttonText,
        },
    });
